import { Box, Flex } from "@chakra-ui/react";
import { OverlayModal, Spacing } from "goi_common";

import { ParlorDetailProps } from "@/types/parlor";
import { useOverlay } from "@toss/use-overlay";
import {
  ColorIcon,
  colors,
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  GoiLogo,
  HStack,
  useResponsive,
  VStack,
} from "goi_common";
import { useCallback, useEffect, useMemo, useState } from "react";
import ImageSlideSection from "./components/ImageSlideSection";

import { useRouter } from "next/router";
import FlowerModalContent from "./components/Modal/FlowerModalContent";
import ParlorFixedButtons from "./components/ParlorFixedButtons";
import StarRating from "./components/TabInfoSection/ReviewInfoTabPanel/StarRating";
import ParlorRequestBox from "./components/ParlorRequestBox/ParlorRequestBox";
import MainInfoSectionMayVersion from "@/containers/Parlor/ParlorDetailContainer/components/MainInfoSectionMayVersion";
import ParlorIntentShortcuts from "@/containers/Parlor/ParlorDetailContainer/components/ParlorIntentShortcuts";
import TabInfoSectionMayVersion from "@/containers/Parlor/ParlorDetailContainer/components/TabInfoSection/TabInfoSectionMayVersion";
import ParlorRevisitExperimentGate from "./components/ParlorRevisitExperimentGate";
import CareFacilityLeadGate from "@/components/CareFacilityLead/CareFacilityLeadGate";
import ParlorCompareExperimentGate from "./components/ParlorCompareExperimentGate";
import { parlorToCompareVisit } from "./utils/parlorVisitPayload";
import { useParlorDetailIsRevisit } from "@/hooks/useParlorDetailIsRevisit";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";

function ParlorDetailContainerMayVersion({
  parlor,
  closestParlorsByService,
  nearbyFacilities,
  discountDescription,
  isExcluded,
  pageDescription,
}: {
  parlor: ParlorDetailProps;
  refetch: any;
  closestParlorsByService: any;
  nearbyFacilities?: any;
  discountDescription?: string;
  isExcluded: boolean;
  pageDescription: string;
}) {
  const { openParlorInquiryModal } = useParlorInquiryModal("parlor_detail");
  const [selectedSangjo, setSelectedSangjo] = useState("가족장");
  const overlay = useOverlay();
  const { isBiggerThanLg } = useResponsive();

  const router = useRouter();

  const isRevisit = useParlorDetailIsRevisit(parlor.id);
  const regionLabel = [
    FUNERAL_SIDO.get(Number(parlor.sido)),
    FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(Number(parlor.sigungu)),
  ]
    .filter(Boolean)
    .join(" ");

  const openFlowerModal = useCallback(
    ({ parlorId }: { parlorId: number }) => {
      overlay.open(({ isOpen, close: onClose }) => {
        return (
          <OverlayModal
            onClose={onClose}
            isOpen={isOpen}
            closeOnOverlayClick={true}
            modalContentStyle={{
              minWidth: "380px",
              minHeight: isBiggerThanLg ? "" : "90vh",
            }}
            overlayModalContent={
              <FlowerModalContent
                companyName={parlor.companyname}
                parlorId={parlorId}
                onClose={onClose}
              />
            }
            header={{
              content: <FlowerModalContent.header onClose={onClose} />,
            }}
          />
        );
      });
    },
    [overlay, parlor, isBiggerThanLg],
  );

  useEffect(() => {
    if (router.query.showFlowerModal === "true") {
      openFlowerModal({
        parlorId: Number(parlor.id),
      });
    }
  }, [router.query, openFlowerModal]);

  const closestParlors = useMemo(() => {
    if (parlor) {
      return closestParlorsByService[selectedSangjo];
    }
  }, [selectedSangjo, parlor]);

  const visitPayload = parlorToCompareVisit(parlor, regionLabel || "");

  return (
    <>
      {isRevisit && (
        <ParlorRevisitExperimentGate
          key={`same-${parlor.id}`}
          parlorId={parlor.id}
          companyName={parlor.companyname}
          region={regionLabel || undefined}
        />
      )}
      {!isRevisit && (
        <CareFacilityLeadGate
          path="parlor_detail"
          facilityName={parlor.companyname}
          sido={parlor.sido}
          sigungu={parlor.sigungu}
        />
      )}
      <ParlorCompareExperimentGate
        key={`compare-${parlor.id}`}
        parlorId={parlor.id}
        companyName={parlor.companyname}
        region={regionLabel || ""}
        imageUrl={visitPayload.imageUrl}
        minPriceManwon={visitPayload.minPriceManwon}
      />
      <VStack width="100%" alignItems="center">
        <VStack width="100%" maxWidth="480px">
          {!isExcluded && (
            <>
              <Box position="relative">
                {discountDescription && (
                  <Box
                    position="absolute"
                    top="20px"
                    left="12px"
                    zIndex="1"
                    background="rgba(0, 0, 0, 0.5)"
                    backdropFilter="blur(2px)"
                    borderRadius="18px"
                    padding="2px 10px 2px 2px"
                  >
                    <HStack gap="4px" alignItems="center" justifyContent="center">
                      <Box
                        margin="auto"
                        background={colors.gray900}
                        padding="4px"
                        borderRadius="50%"
                      >
                        <ColorIcon
                          name="icon-medal-check-mono"
                          color={colors.reviewYellow}
                          width={16}
                          height={16}
                        />
                      </Box>
                      <HStack gap="6px" alignItems="center" justifyContent="center">
                        <GoiLogo color={colors.white} size={16} />
                        <span className="button_12_sb white">빈소 할인 제휴 장례식장</span>
                      </HStack>
                    </HStack>
                  </Box>
                )}

                <ImageSlideSection
                  images={parlor.images}
                  name={parlor.companyname}
                  region={parlor.address?.split(" ").slice(0, 2).join(" ")}
                />
              </Box>
              <MainInfoSectionMayVersion
                sido={parlor.sido}
                sigungu={parlor.sigungu}
                companyName={parlor.companyname}
                address={parlor.address}
                isDiscount={Boolean(discountDescription)}
                id={parlor.id}
                isExcluded={isExcluded}
              />
              <ParlorIntentShortcuts parlor={parlor} />
              {parlor && Number(parlor.id) !== 272 && (
                <TabInfoSectionMayVersion
                  isExcluded={isExcluded}
                  closestParlors={closestParlors}
                  nearbyFacilities={nearbyFacilities}
                  discountDescription={discountDescription}
                  parlor={parlor}
                  selectedSangjo={selectedSangjo}
                  setSelectedSangjo={setSelectedSangjo}
                />
              )}
            </>
          )}
          {isExcluded && (
            <>
              <ImageSlideSection
                images={parlor.images}
                name={parlor.companyname}
                region={parlor.address?.split(" ").slice(0, 2).join(" ")}
              />
              <MainInfoSectionMayVersion
                sido={parlor.sido}
                sigungu={parlor.sigungu}
                companyName={parlor.companyname}
                address={parlor.address}
                isDiscount={Boolean(discountDescription)}
                id={parlor.id}
                isExcluded={isExcluded}
              />
              <Box
                padding="32px"
                margin="30px"
                background={colors.gray100}
                className="subtitle_18_m gray600"
                borderRadius="16px"
              >
                <span className="gray900">
                  해당 장례식장의 요청에 의해
                  <br />
                  가격, 비용정보, 후기, 사진 등의 정보를 제공하지 않습니다.
                  <br />
                </span>
                {
                  FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(Number(parlor.sigungu)) as string
                }{" "}
                지역 장례식장 비용 비교, 지역내 장례식장 정보가 궁금하신 경우
                e하늘장사정보시스템(https://15774129.go.kr/intro.do)
                <br />
                혹은 <b className="orange700">고이 24시간 고객센터{"(1666-9784)"}</b>로 문의해주세요
              </Box>
            </>
          )}
          {String(parlor.id) === "76" && (
            <HStack padding="8px 24px" alignItems="center" gap="24px">
              <StarRating score={3.0} />
              <Box
                className="caption_12_b gray500"
                onClick={() => {
                  window.open("https://place.map.kakao.com/1115026216#comment");
                }}
              >
                카카오맵 후기 <span className="caption_11_b gray800">{"더보기 >"}</span>
              </Box>
            </HStack>
          )}

          {parlor && Number(parlor.id) == 272 && (
            <Box padding="20px 20px">
              <Box
                borderRadius={4}
                background={colors.green500}
                boxShadow="4px 4px 4px #00000044"
                className="subtitle_21_b white"
                padding="16px"
                marginBottom="16px"
                textAlign="center"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_detail",
                      trigger_and_target: "deceased_test0_match_request",
                    },
                  });
                  router.push("/match/request/");
                }}
              >
                온라인 미리 견적(비용 예상) {">>"}
              </Box>
              <Box className="subtitle_18_b gray900" marginBottom="16px">
                조문객
              </Box>
              <HStack gap={12}>
                <Box
                  width="100%"
                  borderRadius={4}
                  background={colors.green400}
                  boxShadow="4px 4px 4px #00000044"
                  className="subtitle_18_b white"
                  padding="16px"
                  marginBottom="16px"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "visit_test1_flower",
                      },
                    });
                    router.replace({
                      query: { ...router.query, showFlowerModal: true },
                    });
                  }}
                >
                  빈소에 화환 보내기
                  <br />
                  <span className="caption_12_b">(심야/야간 주문시 아침 발송)</span>
                </Box>
                <Flex
                  borderRadius={4}
                  background={colors.green400}
                  className="subtitle_18_b white"
                  padding="16px"
                  marginBottom="16px"
                  textAlign="center"
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "visit_test2_info",
                      },
                    });
                    alert(
                      "해당 정보는 개인정보 등에 해당하여 장례식장에 직접 문의해주세요. 장례 준비중이신 경우라면 비용, 위치, 절차 등 하단 미리준비를 눌러주세요",
                    );
                  }}
                >
                  고인 정보, 위치, 주차 문의
                </Flex>
              </HStack>
              <Box className="subtitle_18_b gray900" marginBottom="16px">
                장례 준비, 임종 문의
              </Box>
              <HStack gap={12} width="100%">
                <Box
                  width="100%"
                  borderRadius={4}
                  background={colors.green500}
                  className="subtitle_21_b white"
                  padding="16px"
                  marginBottom="16px"
                  textAlign="center"
                  boxShadow="4px 4px 4px #00000044"
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "deceased_test1_call",
                      },
                    });
                    void openParlorInquiryModal();
                  }}
                >
                  임종 접수
                  <br />
                  이송 요청
                </Box>
                <Box
                  width="100%"
                  borderRadius={4}
                  background={colors.green500}
                  className="subtitle_18_b white"
                  padding="16px"
                  marginBottom="16px"
                  textAlign="center"
                  boxShadow="4px 4px 4px #00000044"
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "deceased_test2_process_chat",
                      },
                    });
                    window.open("http://pf.kakao.com/_xgFDaK/chat");
                  }}
                >
                  미리 준비(채팅)
                  <br />
                  (절차/비용문의)
                </Box>
              </HStack>

              <Box
                borderRadius={4}
                background={colors.green500}
                boxShadow="4px 4px 4px #00000044"
                className="subtitle_18_b white"
                padding="16px"
                marginBottom="16px"
                textAlign="center"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "parlor_detail",
                      trigger_and_target: "deceased_test4_match_request",
                    },
                  });
                  router.push("/match/request/");
                }}
              >
                장례 전체비용, 장례식장, 장지비용 확인
              </Box>
              <Box width="100%" alignItems="center">
                <Box width="100%" alignItems="center">
                  <Box width="100%" maxWidth="480px" padding="12px" margin="auto">
                    <ParlorRequestBox
                      sido={parlor.sido}
                      sigungu={parlor.sigungu}
                      companyName={parlor.companyname}
                    />
                  </Box>
                </Box>
                <Spacing gap={120} />
              </Box>
            </Box>
          )}
        </VStack>
      </VStack>
      <ParlorFixedButtons
        parlor={parlor}
        selectedSangjo={selectedSangjo}
        closestParlors={closestParlors}
      />
    </>
  );
}
export default ParlorDetailContainerMayVersion;
